/**
 * CC5Payment — thin wrapper that renders the unified PaymentSheet for the compat
 * flow. Owns the compat-specific bits: the blurred pair-avatar background, the
 * invite-mint (POST /compat-checks/invite, persisted via SET_INVITE_ID), and the
 * flow.send result handling. Payment UI + coupon come entirely from PaymentSheet.
 */

import { useTranslation } from 'react-i18next';
import { SKU_CATALOG } from '@wds/astro-bot-shared/catalog';
import { useSkuInvoice } from '../../../hooks/useSkuInvoice';
import { apiFetch } from '../../../lib/api-client';
import { useAuth } from '../../../store/auth';
import { PaymentSheet } from '../../payment/PaymentSheet';
import { CCAvatar } from './shared';
import type { UseCompatFlowReturn } from '../../../hooks/useCompatFlow';
import styles from './Compat.module.css';

interface InviteResponse {
  compatInviteId: string;
  inviteLink: string;
}

interface CC5PaymentProps {
  flow: UseCompatFlowReturn;
}

export function CC5Payment({ flow }: CC5PaymentProps): JSX.Element {
  const { t } = useTranslation(['flows', 'common']);
  const { partner, compatInviteId, isGift, searchUsername } = flow;
  const { purchase, redeemCoupon } = useSkuInvoice();
  const jwt = useAuth((s) => s.jwt);

  // gifting_crush (anonymous gift, 50⭐) vs compat_check (self, 60⭐). Same
  // jobType/pipeline — only the SKU code, price and CompatInvite.isGift differ.
  const skuCode = isGift ? 'gifting_crush' : 'compat_check';

  // The recipient handle: a resolved partner (on-platform) or, for an off-platform
  // gift, the raw searched username (the backend stores invitedTelegramUserId=null
  // and the recipient registers via the share link in the bot).
  const recipientUsername = partner?.username ?? (isGift ? searchUsername : '');

  // Mints a CompatInvite (or reuses the one already in flow state). Persists a
  // freshly-minted id via SET_INVITE_ID so a retry does not POST twice. In gift
  // mode it also stamps isGift on the invite and stashes the share link (the
  // gift_sent terminal screen renders it).
  async function mintInvite(): Promise<string | null> {
    let inviteId = compatInviteId;
    if (!inviteId) {
      if (!jwt || !recipientUsername) return null;
      const res = await apiFetch('/api/v1/compat-checks/invite', jwt, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        // Only stamp isGift when true — the backend defaults to false, and the
        // non-gift compat path keeps its original minimal body/contract.
        body: JSON.stringify({ invitedTelegramUsername: recipientUsername, ...(isGift ? { isGift: true } : {}) }),
      });
      if (!res.ok) return null;
      const data = (await res.json()) as InviteResponse;
      inviteId = data.compatInviteId;
      flow.send({ type: 'SET_INVITE_ID', id: inviteId });
      flow.send({ type: 'SET_INVITE_LINK', link: data.inviteLink });
    }
    return inviteId;
  }

  const priceStars = SKU_CATALOG[skuCode].stars;
  const partnerGlyph = partner?.glyph ?? (isGift ? '🎁' : '✦');
  const partnerPalette = partner?.palette ?? 'violet';
  const partnerName =
    partner?.displayName ?? (isGift && searchUsername ? `@${searchUsername}` : t('compat.pair.partnerFallback'));

  const background = (
    <>
      <div className={styles.paymentBlurBg} aria-hidden="true">
        <div style={{ display: 'flex' }}>
          <div style={{ transform: 'rotate(-6deg)' }}>
            <CCAvatar glyph="🌙" palette="gold" size={120} />
          </div>
          <div style={{ transform: 'rotate(6deg) translateX(-12px)' }}>
            <CCAvatar glyph={partnerGlyph} palette={partnerPalette} size={120} />
          </div>
        </div>
      </div>
      <div className={styles.paymentScrim} aria-hidden="true" />
    </>
  );

  return (
    <div data-screen-label="cc-05 Payment confirm">
      <PaymentSheet
        skuCode={skuCode}
        title={isGift ? t('compat.gift.payItemName') : t('compat.payment.itemName')}
        subtitle={isGift ? t('compat.gift.paySubtitle', { name: partnerName }) : `${t('compat.pair.me')} × ${partnerName}`}
        priceStars={priceStars}
        bullets={[isGift ? t('compat.gift.payNote') : t('compat.payment.note')]}
        background={background}
        payTestId="cc5-pay-btn"
        cancelTestId="cc5-cancel-btn"
        onPay={() => {
          void (async () => {
            try {
              const inviteId = await mintInvite();
              if (!inviteId) {
                flow.send({ type: 'ERROR', message: t('compat.payment.errorFailed') });
                return;
              }
              const { status } = await purchase(skuCode, { compatInviteId: inviteId });
              if (status === 'paid') flow.send({ type: 'PAID' });
              else if (status === 'cancelled') flow.send({ type: 'CANCEL' });
              else flow.send({ type: 'ERROR', message: t('compat.payment.errorFailed') });
            } catch {
              flow.send({ type: 'ERROR', message: t('compat.payment.errorFailed') });
            }
          })();
        }}
        onApplyCoupon={async (code) => {
          const inviteId = await mintInvite();
          if (!inviteId) throw new Error('invite_failed');
          await redeemCoupon(skuCode, code, { compatInviteId: inviteId });
          flow.send({ type: 'PAID' });
        }}
        onCancel={() => flow.send({ type: 'CANCEL' })}
      />
    </div>
  );
}

export default CC5Payment;
